// The balances an award can move, and how the till's three fields map onto them.
//
// TWO TYPES, NOT THREE KINDS. This replaces `earnPoints` / `spendPoints` /
// `spendCredits`, which encoded direction in the name and needed a `sign` table
// to get it back out. Direction is a property of the value:
//
//   贈送點數 12    ->  { type: 'point',  value:  +12 }
//   折抵點數 300   ->  { type: 'point',  value: -300 }
//   折抵購物金 50  ->  { type: 'credit', value:  -50 }
//
// A return is entered as a new order with negative quantities, so an earn can
// be a clawback and the sign carries that too. The old per-kind `sign` table
// said otherwise and was wrong for every corrective order.
//
// Adding a balance a vendor supports is one entry in TYPES and one in
// FORM_FIELDS -- no schema change, no new index, no new state field.

const TYPES = ['point', 'credit']

const isType = type => TYPES.includes(type)

const assertType = type => {
  if (!isType(type)) throw new Error(`unknown award type: ${type}`)

  return type
}

// The three fields the cashier form shows, and what each one means.
//
// `sign` IS THE FORM'S CONVENTION, NOT THE DOCUMENT'S. A cashier types how much
// to take off and the field says 折抵; storage keeps a signed delta because the
// provider endpoint reads direction from the sign. This table is the only place
// that knows the two differ.
//
// `spendable` fields refuse a negative: 折抵 -3 has no meaning a cashier would
// intend, and it would silently become a GRANT of 3 once the sign is applied.
//
// `intent` IS STORED ON THE AWARD and is not the same fact as the sign. 贈送點數
// -12 is a clawback on a return and stores the identical `{ point, -12 }` as
// 折抵點數 12, so the box a figure came from cannot be recovered from the number.
const FORM_FIELDS = [
  {
    path: 'earnPoints',
    type: 'point',
    intent: 'earn',
    sign: 1,
    spendable: false,
    label: '贈送點數',
    // THE ONLY BOX WITH A SUGGESTED VALUE, and so the only one a staffer can
    // override. `forcePath` names the screen-state flag that says they did, and
    // having one is what makes this field's award carry `force` -- a 折抵 is
    // always typed, so there is nothing there to override.
    forcePath: 'earnPointsForce',
  },
  {
    path: 'spendPoints',
    type: 'point',
    intent: 'spend',
    sign: -1,
    spendable: true,
    label: '折抵點數',
  },
  {
    path: 'spendCredits',
    type: 'credit',
    intent: 'spend',
    sign: -1,
    spendable: true,
    label: '折抵購物金',
  },
]

const INTENTS = ['earn', 'spend']

const FORM_PATHS = FORM_FIELDS.map(field => field.path)

// The override flags, for the fields that have one. Screen state: declared on
// the cashier view schema, stripped by withoutAwards, and recorded on the award
// itself as `force`.
const FORCE_PATHS = FORM_FIELDS.map(field => field.forcePath).filter(Boolean)

export {
  TYPES,
  INTENTS,
  isType,
  assertType,
  FORM_FIELDS,
  FORM_PATHS,
  FORCE_PATHS,
}
